import React, { useRef, useEffect, useLayoutEffect, ReactNode } from 'react';

interface WheelPickerOption {
    label: string | ReactNode;
    value: string | number;
}

interface WheelPickerProps {
    options: WheelPickerOption[];
    value: string | number;
    onChange: (value: string | number) => void;
    className?: string;
}

const ITEM_HEIGHT = 40; // Height of each item in pixels
const VISIBLE_ITEMS = 5; // Number of items visible at once
const CONTAINER_HEIGHT = ITEM_HEIGHT * VISIBLE_ITEMS;

const WheelPicker: React.FC<WheelPickerProps> = ({ options, value, onChange, className = '' }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const scrollTimeout = useRef<NodeJS.Timeout | undefined>(undefined);
    const isUserScrolling = useRef(false); // Only true when user manually scrolls

    // Calculate index for the current value
    const getSelectedIndex = () => {
        return options.findIndex(opt => opt.value === value);
    };

    // Use layoutEffect for initial positioning (runs before paint)
    useLayoutEffect(() => {
        if (containerRef.current) {
            const index = getSelectedIndex();
            if (index !== -1) {
                // Set scroll position instantly without animation
                containerRef.current.scrollTo({
                    top: index * ITEM_HEIGHT,
                    behavior: 'instant'
                });
            }
        }
    }, []); // Only on mount

    // Sync scroll when value changes externally
    useEffect(() => {
        if (containerRef.current && !isUserScrolling.current) {
            const index = getSelectedIndex();
            if (index !== -1) {
                const targetScroll = index * ITEM_HEIGHT;
                const currentScroll = containerRef.current.scrollTop;
                // Only scroll if position is significantly different
                if (Math.abs(currentScroll - targetScroll) > ITEM_HEIGHT / 2) {
                    containerRef.current.scrollTo({
                        top: targetScroll,
                        behavior: 'instant'
                    });
                }
            }
        }
    }, [value]);

    const handleScroll = () => {
        if (!containerRef.current) return;

        // Mark as user scrolling
        isUserScrolling.current = true;

        const scrollTop = containerRef.current.scrollTop;
        const index = Math.round(scrollTop / ITEM_HEIGHT);

        // Clamp index
        const clampedIndex = Math.max(0, Math.min(index, options.length - 1));
        const selectedOption = options[clampedIndex];

        if (selectedOption && selectedOption.value !== value) {
            onChange(selectedOption.value);
        }

        // Reset user scrolling flag after scroll ends
        clearTimeout(scrollTimeout.current);
        scrollTimeout.current = setTimeout(() => {
            isUserScrolling.current = false;
        }, 150);
    };

    return (
        <div className={`relative ${className}`} style={{ height: CONTAINER_HEIGHT }}>
            {/* Selection Highlight / Overlay */}
            <div
                className="absolute w-full pointer-events-none border-y-2 border-purple-500/60 bg-gradient-to-r from-purple-600/20 via-purple-500/10 to-purple-600/20 z-10 rounded-lg"
                style={{
                    top: '50%',
                    transform: 'translateY(-50%)',
                    height: ITEM_HEIGHT
                }}
            />

            {/* Gradient Masks - using purple gradients to match theme */}
            <div className="absolute top-0 w-full h-1/3 bg-gradient-to-b from-purple-950/90 via-purple-900/50 to-transparent z-10 pointer-events-none" />
            <div className="absolute bottom-0 w-full h-1/3 bg-gradient-to-t from-purple-950/90 via-purple-900/50 to-transparent z-10 pointer-events-none" />

            {/* Scroll Container */}
            <div
                ref={containerRef}
                onScroll={handleScroll}
                className="overflow-y-auto w-full h-full snap-y snap-mandatory hide-scrollbar relative z-0"
                style={{ scrollbarWidth: 'none' }} // Firefox
            >
                {/* Top Padding to center first item */}
                <div style={{ height: (CONTAINER_HEIGHT - ITEM_HEIGHT) / 2 }} />

                {options.map((option) => {
                    const isSelected = option.value === value;
                    return (
                        <div
                            key={option.value}
                            className={`h-[40px] flex items-center justify-center snap-center transition-all duration-200 cursor-pointer relative z-20
                                ${isSelected ? 'text-white font-bold text-lg' : 'text-purple-300/60 text-sm'}`}
                            onClick={() => {
                                // Allow clicking an item to scroll to it
                                const idx = options.indexOf(option);
                                if (containerRef.current) {
                                    containerRef.current.scrollTo({
                                        top: idx * ITEM_HEIGHT,
                                        behavior: 'smooth'
                                    });
                                }
                            }}
                        >
                            {option.label}
                        </div>
                    );
                })}

                {/* Bottom Padding */}
                <div style={{ height: (CONTAINER_HEIGHT - ITEM_HEIGHT) / 2 }} />
            </div>
        </div>
    );
};

export default WheelPicker;
